/**
 * BillingGate — staged dunning UI wrapper (migration 00142).
 *
 * Wraps the page content. Shows a phase banner (grace / warning / read-only) and,
 * once fully locked, replaces the app with a lock wall (Billing page stays
 * reachable so they can pay). Server-side, readonly/locked already block new
 * entries — this is the surfacing layer.
 *
 * @since 1.0.0
 */
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Lock, AlertTriangle, Sparkles, ArrowRight, Eye } from 'lucide-react';
import { useEntitlements, type BillingPhase } from '@/shared/hooks/useEntitlements';

function DunningBanner({ phase, daysOverdue, onGo, preview }: { phase: BillingPhase; daysOverdue: number; onGo: () => void; preview?: boolean }) {
    const lockInDays = Math.max(0, 30 - daysOverdue);

    let cls = 'bg-teal-50 border-teal-200 text-teal-800 dark:bg-teal-950/20 dark:border-teal-900';
    let Icon = Sparkles;
    let msg = '';
    if (phase === 'grace') {
        msg = "You're a valued customer — we've extended a grace period for you to complete your payment. Upgrade to keep everything running.";
    } else if (phase === 'warning') {
        cls = 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-950/20 dark:border-amber-900';
        Icon = AlertTriangle;
        msg = `Action needed: your account will be locked in about ${lockInDays} day${lockInDays === 1 ? '' : 's'} if payment isn't completed. Please upgrade now to avoid interruption.`;
    } else if (phase === 'readonly') {
        cls = 'bg-red-50 border-red-300 text-red-700 dark:bg-red-950/20 dark:border-red-900';
        Icon = Eye;
        msg = `Your account is read-only — you can view and export, but can't add new entries until payment. Full lock in about ${lockInDays} day${lockInDays === 1 ? '' : 's'}.`;
    } else if (phase === 'locked') {
        cls = 'bg-red-50 border-red-300 text-red-700 dark:bg-red-950/20 dark:border-red-900';
        Icon = Lock;
        msg = 'Your account is locked. Complete payment on this page to restore access.';
    }

    return (
        <div className={`flex items-start gap-3 px-4 py-2.5 mb-4 rounded-lg border text-sm ${cls}`}>
            <Icon size={16} className="mt-0.5 shrink-0" />
            <p className="flex-1 leading-relaxed">
                {preview && <span className="inline-block mr-2 px-1.5 py-0.5 rounded bg-stone-900/80 text-white text-[9px] font-bold uppercase tracking-wide align-middle">Admin preview</span>}
                {msg}
            </p>
            <button onClick={onGo} className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold">
                Upgrade <ArrowRight size={13} />
            </button>
        </div>
    );
}

export function BillingGate({ currentRoute, children }: { currentRoute: string; children: ReactNode }) {
    const { phase, daysOverdue, previewPhase } = useEntitlements();
    const navigate = useNavigate();
    const onBilling = currentRoute.startsWith('/settings');
    const go = () => navigate({ to: '/settings', hash: 'billing' });

    // Full lock — block the whole app except the Billing/Settings page.
    if (phase === 'locked' && !onBilling) {
        return (
            <div className="flex items-center justify-center px-4 py-16 min-h-[70vh]">
                <div className="max-w-md w-full text-center rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-8">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center mb-4">
                        <Lock size={22} className="text-red-500" />
                    </div>
                    {previewPhase && <span className="inline-block mb-2 px-2 py-0.5 rounded bg-stone-900/80 text-white text-[10px] font-bold uppercase tracking-wide">Admin preview</span>}
                    <h1 className="text-lg font-semibold text-stone-900 dark:text-stone-100">Your account is locked</h1>
                    <p className="text-sm text-stone-500 mt-2">
                        Your free period and grace window have ended. Complete payment to restore full access — your data is safe and waiting.
                    </p>
                    <button onClick={go} className="inline-flex items-center gap-1.5 mt-6 h-10 px-4 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors">
                        Go to Plan &amp; Billing <ArrowRight size={15} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {phase !== 'active' && <DunningBanner phase={phase} daysOverdue={daysOverdue} onGo={go} preview={!!previewPhase} />}
            {children}
        </>
    );
}
